import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useOffice } from '@/hooks/useOffice';
import { logActivity } from '@/lib/activityLog';

interface Tape {
  id: number;
  media_label: string;
  media_set: string | null;
  modified_date: string | null;
  tape_type: string | null;
  location: string | null;
  box_number: string | null;
  notes: string | null;
  damaged: boolean;
}

export default function DamagedPage() {
  const { currentOfficeId } = useOffice();
  const [tapes, setTapes] = useState<Tape[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const load = async () => {
    if (!currentOfficeId) return;
    setLoading(true);
    setError('');
    try {
      const { data, error } = await supabase
        .from('tapes')
        .select('*')
        .eq('office_id', currentOfficeId)
        .eq('damaged', true)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      setTapes(data as Tape[]);
      setSelected(new Set());
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [currentOfficeId]);

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const allSelected = useMemo(
    () => tapes.length > 0 && tapes.every((t) => selected.has(t.id)),
    [tapes, selected],
  );

  const sortedTapes = useMemo(() => {
    const sorted = [...tapes].sort((a, b) => {
      const la = (a.media_label || '').toLowerCase();
      const lb = (b.media_label || '').toLowerCase();
      if (la < lb) return sortDir === 'asc' ? -1 : 1;
      if (la > lb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [tapes, sortDir]);

  const toggleSort = () => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(tapes.map((t) => t.id)));
  };

  const markRestored = async (ids: number[]) => {
    setRestoring(true);
    setError('');
    try {
      const { error } = await supabase
        .from('tapes')
        .update({ damaged: false, updated_at: new Date().toISOString() })
        .in('id', ids);
      if (error) throw error;

      setTapes((s) => s.filter((t) => !ids.includes(t.id)));
      setSelected(new Set());
      await logActivity('restore_tape', `Restored ${ids.length} tape(s) from damaged`);
    } catch (e: any) {
      setError(e?.message || 'Failed to restore tapes');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Scrapped Tapes</h1>
          <p className="text-sm text-slate-500 mt-1">
            {tapes.length} tapes marked as damaged
          </p>
        </div>
        {selected.size > 0 && (
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600">{selected.size} selected</span>
            <button
              onClick={() => markRestored(Array.from(selected))}
              disabled={restoring}
              className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 cursor-pointer"
            >
              <i className="ri-arrow-go-back-line w-4 h-4 flex items-center justify-center" />
              {restoring ? 'Restoring...' : 'Mark Restored'}
            </button>
          </div>
        )}
      </div>

      <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm flex items-start gap-3">
        <i className="ri-error-warning-line w-5 h-5 flex items-center justify-center shrink-0 mt-0.5" />
        <span>
          These tapes have been marked as <strong>damaged</strong> and are scrapped. Check the box
          next to a tape — or use the <strong>select-all</strong> checkbox in the header — then
          click <strong>Mark Restored</strong> if a tape turns out to be usable again.
        </span>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={load} className="text-xs underline cursor-pointer whitespace-nowrap">
            Retry
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium">
                <button
                  onClick={toggleSort}
                  className="inline-flex items-center gap-1 hover:text-slate-700 cursor-pointer whitespace-nowrap"
                  title="Sort by Media Label"
                >
                  Media Label
                  <i
                    className={
                      sortDir === 'asc'
                        ? 'ri-arrow-up-line w-4 h-4 flex items-center justify-center'
                        : 'ri-arrow-down-line w-4 h-4 flex items-center justify-center'
                    }
                  />
                </button>
              </th>
              <th className="text-left px-4 py-2.5 font-medium">Media Set</th>
              <th className="text-left px-4 py-2.5 font-medium">Tape Type</th>
              <th className="text-left px-4 py-2.5 font-medium">Location</th>
              <th className="text-left px-4 py-2.5 font-medium">Box Number</th>
              <th className="text-left px-4 py-2.5 font-medium">Modified</th>
              <th className="text-left px-4 py-2.5 font-medium">Notes</th>
              <th className="text-center px-4 py-2.5 font-medium">
                <div className="inline-flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={tapes.length === 0}
                    className="w-4 h-4 cursor-pointer accent-emerald-600 disabled:cursor-not-allowed"
                    title="Select all tapes on this page"
                  />
                  <span className="whitespace-nowrap">Restore</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="text-center py-10 text-slate-400 text-sm">Loading...</td>
              </tr>
            )}
            {!loading &&
              sortedTapes.map((t) => (
                <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-medium font-mono">{t.media_label}</td>
                  <td className="px-4 py-2.5 text-slate-600">{t.media_set || '—'}</td>
                  <td className="px-4 py-2.5">
                    {t.tape_type ? (
                      <span className="inline-block px-2 py-0.5 text-xs rounded bg-red-50 text-red-700">
                        {t.tape_type}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{t.location || '—'}</td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs font-mono">{t.box_number || '—'}</td>
                  <td className="px-4 py-2.5 text-slate-600 text-xs">{t.modified_date || '—'}</td>
                  <td className="px-4 py-2.5 text-slate-600 max-w-xs truncate" title={t.notes || ''}>
                    {t.notes || '—'}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <input
                      type="checkbox"
                      checked={selected.has(t.id)}
                      onChange={() => toggleOne(t.id)}
                      className="w-4 h-4 cursor-pointer accent-emerald-600"
                      title="Select this tape to restore"
                    />
                  </td>
                </tr>
              ))}
            {!loading && tapes.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-10 text-slate-400 text-sm">
                  No tapes marked as damaged
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}